import { boxCenter, rotatePoint } from "./layerRotation.js";

// Same kinds as isTextOriginPreviewKind: the copy that fills the box is not
// the string shown in the editor.
const VARIABLE_TEXT_KINDS = new Set(["data", "generated", "computed"]);

const MIN_INTERSECTION_AREA = 1e-6;
// A label sitting inside a button image is the button, not a clash.
const CONTAINED_TEXT_RATIO = 0.9;

export const textCollisionPairKey = (leftID, rightID) => {
  const [first, second] = [String(leftID), String(rightID)].sort();
  return `${first}\u001f${second}`;
};

const isVariableTextBox = (box) =>
  box?.type === "text" && VARIABLE_TEXT_KINDS.has(box.sourceKind);

const isCollisionTarget = (box) => {
  if (!box || box.isBackground) return false;
  return box.type === "text" || box.type === "image";
};

const screenPolygon = (box) => {
  const x = Number(box?.nx) || 0;
  const y = Number(box?.ny) || 0;
  const width = Number(box?.nw) || 0;
  const height = Number(box?.nh) || 0;
  if (width <= 0 || height <= 0) return [];
  const center = boxCenter({ x, y, width, height });
  const rotation = Number(box?.rotation) || 0;
  const corners = [
    { x, y: y + height },
    { x: x + width, y: y + height },
    { x: x + width, y },
    { x, y },
  ];
  if (!rotation) return corners;
  return corners.map((point) =>
    rotatePoint({ point, center, degrees: rotation })
  );
};

const toClipSpace = (point) => ({ x: point.x, y: -point.y });
const fromClipSpace = (point) => ({ x: point.x, y: -point.y });

const cross = (start, end, point) =>
  (end.x - start.x) * (point.y - start.y) -
  (end.y - start.y) * (point.x - start.x);

const segmentIntersection = (start, end, edgeStart, edgeEnd) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const edgeDx = edgeEnd.x - edgeStart.x;
  const edgeDy = edgeEnd.y - edgeStart.y;
  const denom = dx * edgeDy - dy * edgeDx;
  if (Math.abs(denom) < 1e-12) return { x: end.x, y: end.y };
  const t =
    ((edgeStart.x - start.x) * edgeDy - (edgeStart.y - start.y) * edgeDx) /
    denom;
  return { x: start.x + t * dx, y: start.y + t * dy };
};

const clipPolygon = (subject, clipper) => {
  let output = subject;
  for (let edge = 0; edge < clipper.length; edge += 1) {
    const edgeStart = clipper[edge];
    const edgeEnd = clipper[(edge + 1) % clipper.length];
    const input = output;
    output = [];
    if (!input.length) return [];
    for (let index = 0; index < input.length; index += 1) {
      const current = input[index];
      const previous = input[(index + input.length - 1) % input.length];
      const currentInside = cross(edgeStart, edgeEnd, current) >= -1e-9;
      const previousInside = cross(edgeStart, edgeEnd, previous) >= -1e-9;
      if (currentInside) {
        if (!previousInside) {
          output.push(
            segmentIntersection(previous, current, edgeStart, edgeEnd)
          );
        }
        output.push(current);
      } else if (previousInside) {
        output.push(segmentIntersection(previous, current, edgeStart, edgeEnd));
      }
    }
  }
  return output;
};

const polygonArea = (points) => {
  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    sum += current.x * next.y - next.x * current.y;
  }
  return Math.abs(sum) / 2;
};

const boxArea = (box) => {
  const polygon = screenPolygon(box);
  if (polygon.length < 3) return 0;
  return polygonArea(polygon);
};

const containedRatio = (polygon, box) => {
  const area = boxArea(box);
  if (area <= MIN_INTERSECTION_AREA) return 0;
  return polygonArea(polygon) / area;
};

// Full containment is a label on a button, a badge inside a paragraph, or a
// logo sitting inside a text box. Only a partial overlap is a clash.
const isContainedLayout = ({ source, target, polygon }) => {
  if (target?.type !== "image" && target?.type !== "text") return false;
  return (
    containedRatio(polygon, source) >= CONTAINED_TEXT_RATIO ||
    containedRatio(polygon, target) >= CONTAINED_TEXT_RATIO
  );
};

export const intersectLayerBoxes = (left, right) => {
  const subject = screenPolygon(left).map(toClipSpace);
  const clipper = screenPolygon(right).map(toClipSpace);
  if (subject.length < 3 || clipper.length < 3) return null;
  const clipped = clipPolygon(subject, clipper);
  if (clipped.length < 3) return null;
  if (polygonArea(clipped) <= MIN_INTERSECTION_AREA) return null;
  return clipped.map(fromClipSpace);
};

const layerFields = (box) => ({
  name: String(box?.name || ""),
  type: box?.type || "",
});

export const findTextSlotCollisions = ({ boxes = [] } = {}) => {
  const sources = boxes.filter(isVariableTextBox);
  const seen = new Set();
  const collisions = [];

  sources.forEach((source) => {
    boxes.forEach((target) => {
      if (!target || target.id === source.id) return;
      if (!isCollisionTarget(target)) return;
      const key = textCollisionPairKey(source.id, target.id);
      if (seen.has(key)) return;
      const polygon = intersectLayerBoxes(source, target);
      if (!polygon) return;
      if (isContainedLayout({ source, target, polygon })) return;
      seen.add(key);
      const sourceLayer = layerFields(source);
      const targetLayer = layerFields(target);
      collisions.push({
        key,
        sourceID: source.id,
        targetID: target.id,
        sourceName: sourceLayer.name,
        targetName: targetLayer.name,
        sourceType: sourceLayer.type,
        targetType: targetLayer.type,
        polygon,
      });
    });
  });

  collisions.sort((left, right) => left.key.localeCompare(right.key));
  return collisions;
};
